/**
 * Phase 5 dashboard banner for existing (onboarding v0) users.
 *
 * Soft-asks whether the user wants to try the new wizard to
 * double-check their priorities. Purely opt-in — dismissing bumps
 * them to v1 on the backend so the banner doesn't reappear and other
 * v1 features (like the successor prompt + auto-trial) can engage
 * on their next wizard-visible moment.
 *
 * Accepting navigates into the wizard's Profile step. Migration
 * submissions on the backend auto-detect the v0→v1 transition and
 * preserve any existing priority rows (won't clobber Orion-tuned
 * priorities from the legacy flow).
 */
import React, { useCallback, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ActivityIndicator } from 'react-native';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { ms } from '../utils/responsive';
import { onboardingService } from '../services/onboarding';
import { analytics } from '../services/analytics/analytics';
import { AnalyticsEvent } from '../services/analytics/events';
import { createLogger } from '../utils/logger';

const logger = createLogger('MigrationPromptBanner');

export const MigrationPromptBanner: React.FC = () => {
  const navigation = useNavigation<any>();
  const [shouldOffer, setShouldOffer] = useState<boolean | null>(null);
  const [dismissing, setDismissing] = useState(false);

  useFocusEffect(
    useCallback(() => {
      let cancelled = false;
      (async () => {
        const result = await onboardingService.getMigrationStatus();
        if (cancelled) return;
        setShouldOffer(result.shouldOffer);
        if (result.shouldOffer) {
          analytics.track(AnalyticsEvent.MigrationPromptShown);
        }
      })();
      return () => {
        cancelled = true;
      };
    }, []),
  );

  if (shouldOffer === null || shouldOffer === false) return null;

  const handleAccept = () => {
    analytics.track(AnalyticsEvent.MigrationPromptAccepted);
    navigation.navigate('OnboardingWizard', {
      screen: 'WizardProfile',
    });
  };

  const handleDismiss = async () => {
    if (dismissing) return;
    setDismissing(true);
    analytics.track(AnalyticsEvent.MigrationPromptDismissed);
    try {
      await onboardingService.dismissMigrationPrompt();
      // Hide immediately so the user sees it go away without waiting
      // for the next focus refresh
      setShouldOffer(false);
    } catch (err) {
      logger.error('Dismiss failed', err);
      // Hide anyway — failing the API call shouldn't trap the user
      // in a banner they can't close. Next session will re-query.
      setShouldOffer(false);
    } finally {
      setDismissing(false);
    }
  };

  return (
    <View style={styles.banner}>
      <View style={styles.textBlock}>
        <Text style={styles.eyebrow}>Quick update</Text>
        <Text style={styles.title}>Try our new 2-minute setup</Text>
        <Text style={styles.subtitle}>
          We'll help you double-check your priorities without touching anything
          you've already completed.
        </Text>
      </View>
      <View style={styles.actions}>
        <TouchableOpacity
          style={styles.primaryButton}
          onPress={handleAccept}
          accessibilityLabel="Try the new setup"
        >
          <Text style={styles.primaryButtonText}>Try it</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={styles.secondaryButton}
          onPress={handleDismiss}
          disabled={dismissing}
          accessibilityLabel="No thanks, don't ask again"
        >
          {dismissing ? (
            <ActivityIndicator color="#6B7280" size="small" />
          ) : (
            <Text style={styles.secondaryButtonText}>No thanks</Text>
          )}
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  banner: {
    backgroundColor: '#FFFFFF',
    marginHorizontal: ms(12),
    marginTop: ms(10),
    marginBottom: ms(4),
    padding: ms(14),
    borderRadius: ms(12),
    borderWidth: ms(1),
    borderColor: '#29BFC0',
  },
  textBlock: {
    marginBottom: ms(12),
  },
  eyebrow: {
    fontSize: ms(10, 0.3),
    fontWeight: '700',
    color: '#29BFC0',
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    marginBottom: ms(4),
  },
  title: {
    fontSize: ms(15, 0.3),
    fontWeight: '700',
    color: '#1C2C4F',
    marginBottom: ms(4),
  },
  subtitle: {
    fontSize: ms(12, 0.3),
    color: '#4B5563',
    lineHeight: ms(17, 0.3),
  },
  actions: {
    flexDirection: 'row',
    gap: ms(10),
  },
  primaryButton: {
    flex: 1,
    backgroundColor: '#29BFC0',
    paddingVertical: ms(10),
    borderRadius: ms(20),
    alignItems: 'center',
  },
  primaryButtonText: {
    color: '#FFFFFF',
    fontSize: ms(13, 0.3),
    fontWeight: '700',
  },
  secondaryButton: {
    flex: 1,
    paddingVertical: ms(10),
    borderRadius: ms(20),
    alignItems: 'center',
    borderWidth: ms(1),
    borderColor: '#E1E5EA',
  },
  secondaryButtonText: {
    color: '#6B7280',
    fontSize: ms(13, 0.3),
    fontWeight: '600',
  },
});
